"use client";

import { memo } from "react";
import { cn } from "@/lib/utils";
import SearchLocationField from "./fields/SearchLocationField";
import SearchDateField from "./fields/SearchDateField";
import RentalFields from "./fields/RentalFields";
import TravelerPicker from "./fields/TravelerPicker";
import DiscoverFields from "./fields/DiscoverFields";
import SearchSubmitButton from "./SearchSubmitButton";
import { type SearchServiceKey, DEFAULT_SVC_TYPE } from "./search.modules.type";
import {
  useSearchForm,
  type InlineField,
} from "@/hooks/search-form/useSearchForm";

export { DEFAULT_SVC_TYPE };
export type { InlineField };

export type SearchModuleProps = {
  className?: string;
  showSearchLabel?: boolean;
  selectedService?: SearchServiceKey | string;
  onSearchSuccess?: () => void;
  commitOnSearchOnly?: boolean;
  enableInlineExpand?: boolean;
  hideLocationField?: boolean;
  preserveCurrentPathOnSearch?: boolean;
  mode?: "normal" | "compact";
  disableRentalType?: boolean;
  disableVehicleType?: boolean;
  disablePickupType?: boolean;
};

const SearchModule = ({
  className,
  selectedService,
  onSearchSuccess,
  enableInlineExpand = false,
  hideLocationField = false,
  preserveCurrentPathOnSearch = false,
  disableRentalType = false,
  disableVehicleType = false,
  disablePickupType = false,
}: SearchModuleProps) => {
  const {
    activeInlineField,
    setActiveInlineField,
    hasExpandedInlineField,
    isDatePickerOpen,
    setIsDatePickerOpen,
    supportsLocation,
    isHotelSync,
    isTour,
    dateMode,
    extraField,
    comboboxValue,
    hasCoordinates,
    isFetchingLocation,
    hasLocationError,
    showCurrentLocation,
    isComboboxLoading,
    comboboxOptions,
    sectionTitle,
    handleLocationChange,
    handleLocationInputChange,
    handleUseCurrentLocation,
    formSingleDate,
    formRangeValue,
    handleSingleDateChange,
    handleRangeDateChange,
    control,
    handleVehicleTypeChange,
    handlePickupTypeChange,
    formAdults,
    formChildren,
    formRooms,
    handleTravelersChange,
    isPending,
    showReloadIcon,
    handleSearch,
  } = useSearchForm({
    selectedService,
    onSearchSuccess,
    enableInlineExpand,
    preserveCurrentPathOnSearch,
    disableRentalType,
    disableVehicleType,
    disablePickupType,
  });

  return (
    <div
      className={cn(
        "hidden md:flex md:flex-row xl:gap-4 md:gap-2 gap-2",
        hasExpandedInlineField ? "md:items-start" : "md:items-center",
        className,
      )}
    >
      <div
        className={cn(
          "flex flex-1 min-w-0 flex-col md:flex-row xl:gap-4 md:gap-2 gap-2",
          hasExpandedInlineField ? "md:items-start" : "md:items-center",
        )}
      >
        {/* Location Combobox Field */}
        {!hideLocationField && supportsLocation && (
          <SearchLocationField
            isHotel={isHotelSync}
            isTour={isTour}
            value={comboboxValue}
            hasCoordinates={hasCoordinates}
            isFetchingLocation={isFetchingLocation}
            hasError={hasLocationError}
            showCurrentLocation={showCurrentLocation}
            isLoadingOptions={isComboboxLoading}
            options={comboboxOptions}
            sectionTitle={sectionTitle}
            useInlineFieldExpansion={enableInlineExpand}
            inlineExpanded={
              enableInlineExpand && activeInlineField === "location"
            }
            inlineFloating={false}
            onChange={handleLocationChange}
            onInputChange={(val) => void handleLocationInputChange(val)}
            onUseCurrentLocation={handleUseCurrentLocation}
            onInlineExpandedChange={
              enableInlineExpand
                ? (open) => setActiveInlineField(open ? "location" : null)
                : undefined
            }
          />
        )}

        {/* Date Picker Field */}
        {dateMode !== "none" && (
          <SearchDateField
            isSingleDate={dateMode === "single"}
            singleDate={formSingleDate}
            rangeValue={formRangeValue}
            isDatePickerOpen={isDatePickerOpen}
            useInlineFieldExpansion={enableInlineExpand}
            inlineExpanded={enableInlineExpand && activeInlineField === "date"}
            onChangeSingle={handleSingleDateChange}
            onChangeRange={handleRangeDateChange}
            onOpenChange={setIsDatePickerOpen}
            onInlineExpandedChange={
              enableInlineExpand
                ? (open) => setActiveInlineField(open ? "date" : null)
                : undefined
            }
          />
        )}

        {/* Rental-specific Vehicle Type & Pickup Time */}
        {extraField === "rental" && (
          <RentalFields
            control={control}
            disableVehicleType={disableRentalType || disableVehicleType}
            disablePickupType={disableRentalType || disablePickupType}
            onVehicleTypeChange={handleVehicleTypeChange}
            onPickupTypeChange={handlePickupTypeChange}
            useInlineFieldExpansion={enableInlineExpand}
            activeInlineField={activeInlineField}
            onInlineExpandedChange={
              enableInlineExpand
                ? (field) => setActiveInlineField(field)
                : undefined
            }
          />
        )}

        {/* Hotel-specific Traveler Picker */}
        {extraField === "travelers" && (
          <div className="flex-1 min-w-0">
            <TravelerPicker
              value={{
                adults: formAdults,
                children: formChildren,
                rooms: formRooms,
              }}
              onChange={handleTravelersChange}
              inlineExpanded={
                enableInlineExpand && activeInlineField === "traveler"
              }
              onInlineExpandedChange={
                enableInlineExpand
                  ? (open) => setActiveInlineField(open ? "traveler" : null)
                  : undefined
              }
            />
          </div>
        )}

        {/* Discover-specific Fields */}
        {extraField === "discover" && <DiscoverFields />}
      </div>

      {/* Search Submit Button */}
      <SearchSubmitButton
        className={hasExpandedInlineField ? "md:self-start" : undefined}
        isSubmitting={isPending}
        showReloadIcon={showReloadIcon}
        onClick={handleSearch}
      />
    </div>
  );
};

export default memo(SearchModule);
